// src/components/SliderSettingsPanel.jsx
import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useBuilderStore } from "../store/builderStore";

export default function SliderSettingsPanel({ open, onClose, sectionId, question }) {
  const updateQuestion = useBuilderStore((s) => s.updateQuestion);

  const [min, setMin] = useState(0);
  const [max, setMax] = useState(10);
  const [step, setStep] = useState(1);
  const [current, setCurrent] = useState(0);

  // Sync local state when the panel opens or question changes
  useEffect(() => {
    if (!open || !question) return;

    const qMin =
      typeof question.sliderMin === "number" ? question.sliderMin : 0;
    const qMax =
      typeof question.sliderMax === "number" ? question.sliderMax : 10;
    const qStep =
      typeof question.sliderStep === "number" && question.sliderStep > 0
        ? question.sliderStep
        : 1;

    setMin(qMin);
    setMax(qMax);
    setStep(qStep);
    setCurrent(qMin);
  }, [open, question]);

  if (!open || !question) return null;

  const normalizedMin = Number.isFinite(min) ? min : 0;
  const normalizedMax = Number.isFinite(max) ? max : 10;
  const normalizedStep = step > 0 ? step : 1;

  const clampedCurrent = Math.min(
    Math.max(current, normalizedMin),
    normalizedMax
  );

  const handleSave = () => {
    let mMin = Number.isFinite(normalizedMin) ? normalizedMin : 0;
    let mMax = Number.isFinite(normalizedMax) ? normalizedMax : 10;
    let mStep = Number(normalizedStep) || 1;

    if (mStep <= 0) mStep = 1;
    if (mMax <= mMin) {
      mMax = mMin + mStep;
    }

    updateQuestion(sectionId, question.id, {
      sliderMin: mMin,
      sliderMax: mMax,
      sliderStep: mStep,
    });

    onClose?.();
  };

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains("slider-panel-backdrop")) {
      onClose?.();
    }
  };

  return (
    <div className="slider-panel-backdrop" onMouseDown={handleBackdropClick}>
      <div
        className="slider-panel"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="slider-panel-header">
          <div>
            <div className="slider-panel-title">Slider</div>
            <div className="slider-panel-subtitle">
              You can define the range of responses with the slider.
            </div>
          </div>
          <button
            type="button"
            className="slider-close-btn"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>

        <div className="slider-panel-body">
          {/* Preview */}
          <div className="slider-preview">
            <input
              type="range"
              min={normalizedMin}
              max={normalizedMax}
              step={normalizedStep}
              value={clampedCurrent}
              onChange={(e) => setCurrent(Number(e.target.value))}
            />
            <div
              className="slider-preview-scale"
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 4,
                fontSize: 11,
                color: "#6b7280",
              }}
            >
              <span>{normalizedMin}</span>
              <span style={{ fontWeight: 600, color: "#111827" }}>
                {clampedCurrent}
              </span>
              <span>{normalizedMax}</span>
            </div>
          </div>

          {/* Fields */}
          <div className="slider-fields">
            <label className="slider-field">
              <span>Min</span>
              <input
                type="number"
                value={min}
                onChange={(e) => setMin(Number(e.target.value))}
              />
            </label>
            <label className="slider-field">
              <span>Max</span>
              <input
                type="number"
                value={max}
                onChange={(e) => setMax(Number(e.target.value))}
              />
            </label>
            <label className="slider-field">
              <span>Increment</span>
              <input
                type="number"
                min="0.0001"
                step="0.1"
                value={step}
                onChange={(e) => setStep(Number(e.target.value))}
              />
            </label>
          </div>
        </div>

        <div className="slider-panel-footer">
          <button
            type="button"
            className="slider-btn-primary"
            onClick={handleSave}
          >
            Save and apply
          </button>
          <button
            type="button"
            className="slider-btn-ghost"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
